import { snakeCase } from 'lodash';

import {
  amplitudeTrackEvent,
  debugSessionReplay,
  incrementAmplitudeUserPropertyCampaignsGenerated,
  initAmplitude,
  setAmplitudeUserPropertyBillingCycle,
  setAmplitudeUserPropertyCampaignsGenerated,
  setAmplitudeUserPropertyConversionStage,
  setAmplitudeUserPropertyLastActiveDate,
  setAmplitudeUserPropertySignedUpDate,
  setAmplitudeUserPropertySignupSource,
  setAmplitudeUserPropertyUserId,
} from './amplitude';
import {
  AnalyticsEvents,
  BillingCycleType,
  ConversionStageType,
  SocialPlatformType,
} from './events';
import { Creativity, RequestType, SectionName, TabName } from '../types';
import {
  googleAnalyticsTrackEvent,
  setGAUserPropertyBillingCycle,
  setGAUserPropertyConversionStage,
  setGAUserPropertyLastActiveDate,
  setGAUserPropertySignedUpDate,
  setGAUserPropertySignupSource,
  setGAUserPropertyUserId,
} from './google_analytics';
import {
  initSentry,
  setSentryUser,
  setSentryUserBillingCycle,
  setSentryUserConversionStage,
  setSentryUserLastActiveDate,
  setSentryUserSignupDate,
  setSentryUserSignupSource,
} from './sentry';

// ###################################################################################
// # LOCAL UTILITY FUNCTIONS                                                         #
// ###################################################################################

/**
 * Initializes all analytics services.
 * This should be called once when the application starts.
 *
 * - Google Analytics is initialized in firebaseUtils.ts
 * - Sentry is initialized here
 * - Amplitude is initialized here
 */
export function initAnalytics() {
  initSentry();
  initAmplitude();
}

/**
 * Remove any keys with `undefined` values from an object.
 *
 * @param obj - The input object
 * @returns A shallow copy of the object without undefined fields
 *
 * Example:
 * omitUndefined({ category: 'campaign', label: undefined })
 * // => { category: 'campaign' }
 */
export function omitUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(obj) as Array<keyof T>) {
    const v = obj[k];
    if (v !== undefined) out[k] = v as T[typeof k];
  }
  return out;
}

/**
 * Standardized structure for tracking events across multiple analytics platforms.
 *
 * - `name`: Event type identifier (required).
 * - `category`: Logical grouping of the event (optional).
 * - `label`: Descriptive label for the event (optional).
 * - `value`: Numeric only (e.g., cost, score, count).
 * - `properties`: Free-form key/value pairs for additional details, primarily for Amplitude.
 * - `isRawEvent`: If true, disables automatic snake_case formatting for `category` and `label`.
 */
interface TrackEventArgs {
  name: string;
  category?: string;
  label?: string;
  value?: number;
  isRawEvent?: boolean;
  properties?: Record<string, string | number | boolean | null>;
}

/**
 * Track an event across Google Analytics and Amplitude with consistent naming.
 *
 * - Converts event name, category, and label into snake_case by default.
 * - Sends numeric `value` to GA and Amplitude.
 * - Sends extra attributes via `properties` only to Amplitude.
 *
 * @param args - Object containing event details
 * @returns Promise that resolves when all analytics platforms have processed the event
 */
async function trackEvent({
  name,
  category,
  label,
  value,
  isRawEvent = false,
  properties = {},
}: TrackEventArgs): Promise<void> {
  const eventNameFinal = snakeCase(name?.toLowerCase());
  const eventCategoryFinal = category
    ? isRawEvent
      ? category
      : snakeCase(category.toLowerCase())
    : undefined;
  const eventLabelFinal = label
    ? isRawEvent
      ? label
      : snakeCase(label.toLowerCase())
    : undefined;

  const trackingPromises: Promise<void>[] = [];

  try {
    // Google Analytics
    trackingPromises.push(
      Promise.resolve(
        googleAnalyticsTrackEvent({
          name: eventNameFinal,
          category: eventCategoryFinal,
          label: eventLabelFinal,
          value,
        })
      )
    );

    // Amplitude
    trackingPromises.push(
      Promise.resolve(
        amplitudeTrackEvent(eventNameFinal, {
          category: eventCategoryFinal,
          label: eventLabelFinal,
          value,
          properties,
        })
      )
    );

    // Wait for all analytics platforms to process the event
    await Promise.all(trackingPromises);
  } catch (error) {
    console.error(
      '[App | Analytics] Failed to track event across all platforms:',
      error
    );
    throw error;
  }
}

// ###################################################################################
// # TRACKING EVENTS FUNCTIONS                                                       #
// ###################################################################################

/**
 * Get signup source from stored UTM parameters
 * @returns Formatted signup source string or 'direct'
 */
function getSignupSourceFromUTM(): string {
  try {
    const storedAttribution = localStorage.getItem('signup_attribution');
    if (!storedAttribution) {
      return 'direct';
    }

    const utmData = JSON.parse(storedAttribution);
    const { utm_source, utm_medium, utm_campaign } = utmData;

    if (!utm_source) {
      return 'direct';
    }

    // Format: "source_medium_campaign" or just "source"
    if (utm_medium && utm_campaign) {
      return `${utm_source}_${utm_medium}_${utm_campaign}`;
    } else if (utm_medium) {
      return `${utm_source}_${utm_medium}`;
    } else {
      return utm_source;
    }
  } catch (error) {
    console.warn('[App | Analytics] Error parsing stored UTM data:', error);
    return 'direct';
  }
}

/**
 * Track user sign-up and set analytics identifiers.
 *
 * - Sends `sign_up` event to GA and Amplitude.
 * - Sets GA user ID for attribution.
 * - Sets Amplitude user property for signup date.
 *
 * @param userId - Unique user identifier
 */
export async function trackSignUp(userId: string, category: string) {
  if (userId) {
    try {
      const properties: Record<string, string | number | boolean | null> = {
        [AnalyticsEvents.SIGN_UP.properties.METHOD]:
          AnalyticsEvents.SIGN_UP.values.METHOD.GOOGLE,
        [AnalyticsEvents.SIGN_UP.properties.USER_ID]: userId,
      };
      if (category) {
        properties[AnalyticsEvents.SIGN_UP.properties.CATEGORY] = category;
      }
      await trackEvent({
        name: AnalyticsEvents.SIGN_UP.name,
        properties,
      });
      setUserPropertyUserId(userId);
      setUserPropertySignedUpDate();
      setUserPropertySignupSource(); // Set signup source from UTM data
    } catch (error) {
      console.error('[App | Analytics] Error tracking sign up:', error);
    }
  } else {
    console.warn('trackSignIn: userId is undefined');
  }
}

/**
 * Tracks a click on the "Privacy Policy" link, typically in the footer.
 */
export async function trackClickPrivacyPolicy() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_PRIVACY_POLICY.name,
    category: AnalyticsEvents.CLICK_PRIVACY_POLICY.category,
  });
}

/**
 * Tracks a click on the "Terms of Service" link, typically in the footer.
 */
export async function trackClickTermsOfService() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_TERMS_OF_SERVICE.name,
    category: AnalyticsEvents.CLICK_TERMS_OF_SERVICE.category,
  });
}

/**
 * Tracks a click on the "Cookie Policy" link, typically in the footer.
 */
export async function trackClickCookiePolicy() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_COOKIE_POLICY.name,
    category: AnalyticsEvents.CLICK_COOKIE_POLICY.category,
  });
}

/**
 * Tracks a click on the user's profile icon or button.
 */
export async function trackClickProfile() {
  await trackEvent({ name: AnalyticsEvents.CLICK_PROFILE.name });
}

/**
 * Tracks when the tooltip for manual mode is displayed.
 */
export async function trackShowTooltipManualMode() {
  await trackEvent({ name: AnalyticsEvents.SHOW_TOOLTIP_MANUAL_MODE.name });
}

/**
 * Tracks a click on the "Logout" button within the profile section.
 */
export async function trackClickLogoutProfile() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_LOGOUT.name,
    category: AnalyticsEvents.CLICK_LOGOUT.category,
  });
}

/**
 * Track when the support chat is shown or hidden.
 *
 * - Uses numeric `value` (1/0) for GA.
 * - Sends `user_id` as property for Amplitude.
 *
 * @param userId - Unique user identifier
 * @param isShown - True if the chat is shown, false if hidden
 */
export async function trackShowSupportChat(userId: string, isShown: boolean) {
  await trackEvent({
    name: AnalyticsEvents.SHOW_SUPPORT_CHAT.name,
    category: AnalyticsEvents.SHOW_SUPPORT_CHAT.category,
    value: isShown ? 1 : 0,
    properties: { [AnalyticsEvents.SHOW_SUPPORT_CHAT.properties.USER_ID]: userId },
  });
}

/**
 * Track click on a social media icon in the header.
 *
 * @param platform - Social platform name (linkedin | twitter | facebook)
 */
export async function trackClickSocialHeader(platform: SocialPlatformType) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_SOCIAL.name,
    category: AnalyticsEvents.CLICK_SOCIAL.category,
    label: platform,
  });
}

/**
 * Track user sign out action and clear analytics identities.
 *
 * - Sends `sign_out` event to GA and Amplitude with user_id property
 * - Clears user identities from all analytics platforms
 * - Should be called before Firebase Auth signOut
 *
 * @param userId - The user's unique identifier
 * @param location - Where the sign out was triggered from ('header' or 'profile_screen')
 */
export async function trackSignOut(
  userId: string,
  location: 'header' | 'profile_screen'
) {
  if (!userId) {
    console.warn('[App | Analytics] trackSignOut: userId is required');
    return;
  }

  try {
    await trackEvent({
      name: AnalyticsEvents.SIGN_OUT.name,
      category: location,
      properties: { [AnalyticsEvents.SIGN_OUT.properties.USER_ID]: userId },
    });

    console.log('[App | Analytics] Tracked sign out for user:', userId);
  } catch (error) {
    console.error('[App | Analytics] Error tracking sign out:', error);
    throw error;
  }
}

// ###################################################################################
// # USER PROPERTIES FUNCTIONS                                                       #
// ###################################################################################

/**
 * Sets the user ID across all integrated analytics services.
 *
 * - Associates subsequent events with this user.
 * - Sets the `user_id` property in Amplitude and GA.
 * - Identifies the user in Sentry for error tracking.
 *
 * @param userId - The user's unique identifier.
 */
function setUserPropertyUserId(userId: string) {
  setAmplitudeUserPropertyUserId(userId);
  setGAUserPropertyUserId(userId);
  setSentryUser({ id: userId });
}

/**
 * Sets the 'signup_date' user property to the current date across all services.
 * @private
 */
function setUserPropertySignedUpDate() {
  setAmplitudeUserPropertySignedUpDate();
  setGAUserPropertySignedUpDate();
  setSentryUserSignupDate();
}

/**
 * Sets the 'conversion_stage' user property across all services.
 *
 * @param conversionStage - The user's current plan or conversion stage.
 *   Possible values:
 *     - "advance_annual"
 *     - "advance_lifetime"
 *     - "advance_monthly"
 *     - "begin_annual"
 *     - "begin_lifetime"
 *     - "begin_monthly"
 *     - "conquer_annual"
 *     - "conquer_lifetime"
 *     - "conquer_monthly"
 *     - "free"
 *     - "not_registered"
 */
export function setUserPropertyConversionStage(conversionStage: ConversionStageType) {
  setAmplitudeUserPropertyConversionStage(conversionStage);
  setGAUserPropertyConversionStage(conversionStage);
  setSentryUserConversionStage(conversionStage);
}

/**
 * Increments the 'campaigns_generated' user property by one across all analytics services.
 *
 * This function calls the respective increment handlers for Amplitude, Google Analytics,
 * and Sentry to ensure the count is consistently updated.
 */
export function incrementUserPropertyCampaignsGenerated() {
  incrementAmplitudeUserPropertyCampaignsGenerated();
}

/**
 * Sets the absolute value of campaigns_generated across all analytics platforms.
 * Used when syncing from Firestore backend data.
 * @param {number} count - The absolute count to set
 */
export function setUserPropertyCampaignsGenerated(count: number) {
  setAmplitudeUserPropertyCampaignsGenerated(count);
  // Note: GA and Sentry will also receive this update
}

/**
 * Sets the 'signup_source' user property across all services.
 *
 * @param source - Optional source string. If not provided, will extract from stored UTM data.
 */
export function setUserPropertySignupSource(source?: string) {
  const signupSource = source || getSignupSourceFromUTM();

  setAmplitudeUserPropertySignupSource(signupSource);
  setGAUserPropertySignupSource(signupSource);
  setSentryUserSignupSource(signupSource);

  console.log('[App | Analytics] Set signup source:', signupSource);
}

/**
 * Sets the 'last_active_date' user property to the current date across all services.
 */
export function setUserPropertyLastActiveDate() {
  setAmplitudeUserPropertyLastActiveDate();
  setGAUserPropertyLastActiveDate();
  setSentryUserLastActiveDate();
}

/**
 * Sets the 'billing_cycle' user property across all services.
 *
 * @param cycle - The user's billing cycle (e.g., "monthly", "yearly", "lifetime").
 */
export function setUserPropertyBillingCycle(cycle: BillingCycleType) {
  setAmplitudeUserPropertyBillingCycle(cycle);
  setGAUserPropertyBillingCycle(cycle);
  setSentryUserBillingCycle(cycle);
}

/**
 * Track successful Stripe purchase event across all analytics platforms.
 * This function is called when the frontend receives a purchase event from Firestore
 * (triggered by backend Stripe webhook processing).
 *
 * @param eventData - Purchase event data from Firestore (flattened structure)
 * @returns Promise that resolves when all analytics platforms have processed the event
 */
export async function trackStripePurchaseSuccessful(eventData: {
  checkout_session_id: string;
  user_id: string;
  amount: number;
  currency: string;
  plan_name: string;
  plan_code: string;
  subscription_id?: string;
  customer_id: string;
  purchase_number: number;
  is_upgrade: boolean;
  previous_plan?: string;
  payment_method: string;
}): Promise<void> {
  const properties = {
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.CHECKOUT_SESSION_ID]:
      eventData.checkout_session_id,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.USER_ID]:
      eventData.user_id,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.AMOUNT]: eventData.amount,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.CURRENCY]:
      eventData.currency,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.PLAN_NAME]:
      eventData.plan_name,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.PLAN_CODE]:
      eventData.plan_code,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.SUBSCRIPTION_ID]:
      eventData.subscription_id || null,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.CUSTOMER_ID]:
      eventData.customer_id,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.PURCHASE_NUMBER]:
      eventData.purchase_number,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.IS_UPGRADE]:
      eventData.is_upgrade,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.PREVIOUS_PLAN]:
      eventData.previous_plan || null,
    [AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.properties.PAYMENT_METHOD]:
      eventData.payment_method,
  };

  try {
    await trackEvent({
      name: AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.name,
      category: AnalyticsEvents.STRIPE_PURCHASE_SUCCESSFUL_3.category,
      value: eventData.amount / 100, // Convert from cents to dollars for GA
      properties: properties,
    });

    console.log(
      `[App | Analytics] Tracked ${eventData.is_upgrade ? 'upgrade' : 'purchase'} event:`,
      {
        session: eventData.checkout_session_id,
        amount: eventData.amount,
        plan: eventData.plan_name,
        purchaseNumber: eventData.purchase_number,
      }
    );
  } catch (error) {
    console.error(
      `[App | Analytics] Failed to track ${eventData.is_upgrade ? 'upgrade' : 'purchase'} event:`,
      error
    );
    throw error;
  }
}

/**
 * Tracks a click on the credits/requests counter button in the header.
 */
export async function trackClickButtonCredits() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_BUTTON_CREDITS.name,
    category: AnalyticsEvents.CLICK_BUTTON_CREDITS.category,
  });
}

/**
 * Tracks a click on the "New Campaign" button from various locations.
 * @param location - Where the button was clicked from ('header' or 'campaigns')
 */
export async function trackClickButtonNewCampaign(location: 'header' | 'campaigns') {
  await trackEvent({
    name: AnalyticsEvents.CLICK_BUTTON_NEW_CAMPAIGN.name,
    category: location,
  });
}

/**
 * Tracks a click on the profile button in the profile dropdown menu.
 */
export async function trackClickButtonProfile() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_BUTTON_PROFILE.name,
    category: AnalyticsEvents.CLICK_BUTTON_PROFILE.category,
  });
}

/**
 * Tracks a click on any campaign in the campaigns list.
 */
export async function trackClickCampaign() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_CAMPAIGN.name,
  });
}

/**
 * Tracks a click on the delete button for a campaign.
 * @param location - Where the delete button was clicked from (typically 'campaigns_list/campaign')
 */
export async function trackClickCampaignDelete(
  location: string = 'campaigns_list/campaign'
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_CAMPAIGN_DELETE.name,
    category: location,
  });
}

/**
 * Tracks a click on the rename button for a campaign.
 * @param location - Where the rename button was clicked from (typically 'campaigns_list/campaign')
 */
export async function trackClickCampaignRename(
  location: string = 'campaigns_list/campaign'
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_CAMPAIGN_RENAME.name,
    category: location,
  });
}

/**
 * Tracks a click on the three dots menu for a campaign.
 * @param location - Where the three dots menu was clicked from (typically 'campaigns_list/campaign')
 */
export async function trackClickCampaignThreeDotsMenu(
  location: string = 'campaigns_list/campaign'
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_CAMPAIGN_THREE_DOTS_MENU.name,
    category: location,
  });
}

/**
 * Tracks when user confirms tab generation for a campaign section.
 * @param sectionName - The campaign section name (e.g., 'ads', 'strategy', 'smm')
 * @param tabName - The specific tab name (e.g., 'facebook', 'google', 'instagram')
 * @param creditseCost - The cost in credits for generation
 * @param creativityLevel - The creativity level selected by user
 * @param prompt - The prompt/comments provided by user (optional)
 */
export async function trackClickGenerateTabConfirmation(
  sectionName: SectionName,
  tabName: TabName,
  creditseCost?: number,
  creativityLevel?: Creativity,
  prompt?: string
) {
  const properties: Record<string, any> = {};

  if (creativityLevel !== undefined) {
    properties.creativity_level = creativityLevel;
  }
  if (prompt !== undefined && prompt.trim()) {
    properties.prompt = prompt;
  }

  await trackEvent({
    name: AnalyticsEvents.CLICK_GENERATE_TAB_CONFIRMATION.name,
    category: sectionName,
    label: tabName,
    value: creditseCost,
    properties,
  });
}

/**
 * Tracks clicks on generated tabs within campaign sections.
 * @param sectionName - The campaign section name (e.g., 'ads', 'strategy', 'smm')
 * @param tabName - The specific tab name (e.g., 'facebook', 'google', 'instagram')
 */
export async function trackClickGeneratedTab(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_GENERATED_TAB.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks clicks on video guide/tutorial buttons.
 * @param sectionName - The campaign section name (e.g., 'ads', 'strategy', 'smm')
 * @param tabName - The specific tab name (e.g., 'facebook', 'google', 'instagram')
 */
export async function trackClickVideoGuide(
  sectionName: SectionName,
  tabName: TabName | null
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_VIDEO_GUIDE.name,
    category: sectionName,
    label: tabName || undefined,
  });
}

/**
 * Tracks clicks on the profile icon in the header.
 */
export async function trackClickIconProfile() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_ICON_PROFILE.name,
    category: AnalyticsEvents.CLICK_ICON_PROFILE.category,
  });
}

/**
 * Tracks clicks on the "Manage Subscription" button in the profile screen.
 */
export async function trackClickManageSubscription() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_MANAGE_SUBSCRIPTION.name,
    category: AnalyticsEvents.CLICK_MANAGE_SUBSCRIPTION.category,
  });
}

/**
 * Tracks clicks on the "My Campaigns" navigation in the header.
 */
export async function trackClickMyCampaigns() {
  console.log('🎯 Analytics Event: click_my_campaigns triggered');
  await trackEvent({
    name: AnalyticsEvents.CLICK_MY_CAMPAIGNS.name,
    category: AnalyticsEvents.CLICK_MY_CAMPAIGNS.category,
  });
}

/**
 * Tracks clicks on profile tabs (personal details, subscription, etc.).
 * @param tabName - The name of the profile tab (e.g., 'account', 'subscription')
 */
export async function trackClickProfileTab(tabName: string) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_PROFILE_TAB.name,
    category: AnalyticsEvents.CLICK_PROFILE_TAB.category,
    label: tabName,
  });
}

/**
 * Tracks clicks on campaign sections.
 * @param sectionName - The section name (e.g., 'ads', 'strategy', 'smm')
 */
export async function trackClickSection(sectionName: string) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_SECTION.name,
    category: sectionName,
  });
}

/**
 * Tracks clicks on copy buttons for campaign items.
 * @param sectionName - The campaign section name (e.g., 'ads', 'strategy', 'smm')
 * @param tabName - The specific tab name (e.g., 'facebook', 'google', 'instagram')
 * @param itemName - The specific item name (e.g., 'Google Ads Barriers')
 */
export async function trackClickCopy(
  sectionName: SectionName,
  tabName: TabName,
  itemName: RequestType
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_COPY.name,
    category: sectionName,
    label: tabName,
    properties: {
      item_name: itemName,
    },
  });
}

/**
 * Tracks clicks on regenerate buttons for campaign items.
 * @param sectionName - The campaign section name (e.g., 'ads', 'strategy', 'smm')
 * @param tabName - The specific tab name (e.g., 'facebook', 'google', 'instagram')
 * @param itemName - The specific item name (e.g., 'Google Ads Barriers')
 * @param regenerationsCount - The number of times this item has been regenerated
 */
export async function trackClickRegenerate(
  sectionName: SectionName,
  tabName: TabName,
  itemName: RequestType,
  regenerationsCount: number
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_REGENERATE.name,
    category: sectionName,
    label: tabName,
    properties: {
      item_name: itemName,
      regenerations_count: regenerationsCount,
    },
  });
}

/**
 * Tracks clicks on the "Regenerate" confirm button in regenerate popup.
 * @param sectionName - The campaign section name (e.g., 'ads', 'strategy', 'smm')
 * @param tabName - The specific tab name (e.g., 'facebook', 'google', 'instagram')
 * @param itemName - The specific item name (e.g., 'Google Ads Barriers')
 * @param creditssCost - The cost in credits for regeneration
 * @param creativityLevel - The creativity level selected by user (optional)
 * @param prompt - The prompt/comments provided by user (optional)
 * @param regenerationsCount - The number of times this item has been regenerated (optional)
 */
export async function trackClickRegenerateConfirm(
  sectionName: SectionName,
  tabName: TabName,
  itemName: RequestType,
  creditssCost: number,
  creativityLevel?: Creativity,
  prompt?: string,
  regenerationsCount?: number
) {
  const properties: Record<string, any> = {
    item_name: itemName,
  };

  // Only add optional properties if they are provided and valid
  if (creativityLevel !== undefined) {
    properties.creativity_level = creativityLevel;
  }
  if (prompt && prompt.trim()) {
    properties.prompt = prompt;
  }
  if (regenerationsCount !== undefined) {
    properties.regenerations_count = regenerationsCount;
  }

  await trackEvent({
    name: AnalyticsEvents.CLICK_REGENERATE_CONFIRM.name,
    category: sectionName,
    label: tabName,
    value: creditssCost,
    properties,
  });
}

/**
 * Tracks clicks on the close button of the legacy Facebook banner.
 */
export async function trackClickBannerStructuredFbClose() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_BANNER_STRUCTURED_FB_CLOSE.name,
    category: AnalyticsEvents.CLICK_BANNER_STRUCTURED_FB_CLOSE.category,
  });
}

/**
 * Tracks clicks on the CTA button of the legacy Facebook banner.
 */
export async function trackClickBannerStructuredFbCTA() {
  await trackEvent({
    name: AnalyticsEvents.CLICK_BANNER_STRUCTURED_FB_CTA.name,
    category: AnalyticsEvents.CLICK_BANNER_STRUCTURED_FB_CTA.category,
  });
}

/**
 * Tracks when user confirms generation of a new campaign.
 * @param location - Where the confirmation was triggered from ('header' or 'campaigns')
 */
export async function trackClickGenerateCampaignConfirmation(
  location: 'header' | 'campaigns'
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_GENERATE_CAMPAIGN_CONFIRMATION.name,
    category: location,
  });
}

/**
 * Tracks when user discards/hides an idea for structured output.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredItemDiscard(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_ITEM_DISCARD.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks when user clicks "Generate" on any item of structured output.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredItemGenerate(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_ITEM_GENERATE.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks when user cancels generation of an item of structured output.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredItemGenerateCancel(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_ITEM_GENERATE_CANCEL.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks when user confirms generation of an item of structured output.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredItemGenerateConfirm(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_ITEM_GENERATE_CONFIRM.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks when user clicks "generate all posts/items" button.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 * @param creditsPrice - The cost in credits for generating all items
 */
export async function trackClickStructuredGenerateAll(
  sectionName: SectionName,
  tabName: TabName,
  creditsPrice: number
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_GENERATE_ALL.name,
    category: sectionName,
    label: tabName,
    value: creditsPrice,
  });
}

/**
 * Tracks when user cancels generation of all items in structured output.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 * @param creditsPrice - The cost in credits for generating all items
 */
export async function trackClickStructuredGenerateAllCancel(
  sectionName: SectionName,
  tabName: TabName,
  creditsPrice: number
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_GENERATE_ALL_CANCEL.name,
    category: sectionName,
    label: tabName,
    value: creditsPrice,
  });
}

/**
 * Tracks when user confirms generation of all items in structured output.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 * @param creditsPrice - The cost in credits for generating all items
 */
export async function trackClickStructuredGenerateAllConfirm(
  sectionName: SectionName,
  tabName: TabName,
  creditsPrice: number
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_GENERATE_ALL_CONFIRM.name,
    category: sectionName,
    label: tabName,
    value: creditsPrice,
  });
}

/**
 * Tracks when user clicks "More ideas" button.
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredMoreIdeas(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_MORE_IDEAS.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks when user cancels generation of "More ideas".
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredMoreIdeasCancel(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_MORE_IDEAS_CANCEL.name,
    category: sectionName,
    label: tabName,
  });
}

/**
 * Tracks when user confirms generation of "More ideas".
 * @param sectionName - The campaign section name (e.g., 'smm', 'ads')
 * @param tabName - The specific tab name (e.g., 'facebook', 'instagram')
 */
export async function trackClickStructuredMoreIdeasConfirm(
  sectionName: SectionName,
  tabName: TabName
) {
  await trackEvent({
    name: AnalyticsEvents.CLICK_STRUCTURED_MORE_IDEAS_CONFIRM.name,
    category: sectionName,
    label: tabName,
  });
}

// ###################################################################################
// # SESSION REPLAY UTILITIES                                                        #
// ###################################################################################

export { debugSessionReplay };
